'use client'
import {
  AccessibilityButton_default
} from "./chunk-VP7YPBMV.mjs";
import {
  TicketingIcon_default
} from "./chunk-KBZMCWX5.mjs";
import {
  Button_default
} from "./chunk-5MKPTLDW.mjs";
import {
  EventBuyButton_default,
  EventStartPrice_default
} from "./chunk-G5III2IX.mjs";
import {
  NotifyButton_default
} from "./chunk-OE26I2CU.mjs";
import {
  useBooking_default
} from "./chunk-K7LWWBGC.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/components/events/TicketDetails.tsx
import { useGetTickets } from "@wbk/api/hooks";
import { useRouter, useTranslation as useTranslation2 } from "@wbk/config";
import {
  Button,
  Carousel,
  Gallery,
  ImageWithFallback,
  Paper,
  ResponsiveModal,
  Typography as Typography2
} from "@wbk/ui";
import { twMerge as twMerge2 } from "tailwind-merge";
import { CurrencyPriceLabel } from "@wbk/currency";
import { ExclamationIcon, ExpandIcon } from "@wbk/svg";

// src/components/common/TicketTypeName.tsx
import { useTranslation } from "@wbk/config";
import { Typography } from "@wbk/ui";
import { twMerge } from "tailwind-merge";
import { jsx, jsxs } from "react/jsx-runtime";
var TicketTypeName = ({ zone, ticket, typographyProps, showIcon = false, className }) => {
  var _a, _b, _c;
  const { t } = useTranslation("ticketing_booking");
  const includesEntry = !!zone && !(ticket == null ? void 0 : ticket.requires_entry_ticket);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: twMerge(
        "text-text-secondary data-[zone=true]:text-notice flex items-center gap-2 text-sm",
        className
      ),
      "data-zone": includesEntry,
      children: [
        !!((_a = ticket == null ? void 0 : ticket.subscription_ticket_type) == null ? void 0 : _a.icon) && showIcon && /* @__PURE__ */ jsx("img", { src: ticket.subscription_ticket_type.icon, alt: "", className: "h-5 w-5" }),
        /* @__PURE__ */ jsxs(Typography, __spreadProps(__spreadValues({}, typographyProps), { children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              style: {
                color: includesEntry ? "" : (_b = ticket == null ? void 0 : ticket.subscription_ticket_type) == null ? void 0 : _b.background_color
              },
              children: (_c = ticket == null ? void 0 : ticket.subscription_ticket_type) == null ? void 0 : _c.title
            }
          ),
          includesEntry && ` ${t("ticketing_booking:zone_entry.plus_zone_entry")}`
        ] }))
      ]
    }
  );
};
var TicketTypeName_default = TicketTypeName;

// src/components/events/TicketDetails.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var TicketDetailsSection = ({ slug, className, type = "event" }) => {
  var _a, _b, _c, _d;
  const { t } = useTranslation2(["ticketing_event", "ticketing_booking"]);
  const { locale } = useRouter();
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useGetTickets({
    lang: locale,
    slug,
    ticketType: type
  });
  const preview = data == null ? void 0 : data.event.preview_image;
  const feeAmount = Number((_a = data == null ? void 0 : data.event.fees) == null ? void 0 : _a.amount) || 0;
  const feeVat = Number((_b = data == null ? void 0 : data.event.fees) == null ? void 0 : _b.vat) || 0;
  const fee = feeAmount + feeVat;
  if (isLoading || !data || !(data == null ? void 0 : data.tickets.length))
    return null;
  return /* @__PURE__ */ jsxs2("div", { className: twMerge2("space-y-4", className), children: [
    /* @__PURE__ */ jsx2(Typography2, { as: "h2", variant: "heading-S", children: t("ticketing_event:tickets") }),
    !!preview && /* @__PURE__ */ jsxs2(Fragment, { children: [
      /* @__PURE__ */ jsxs2(Paper, { className: "relative p-4", children: [
        /* @__PURE__ */ jsx2(
          ImageWithFallback,
          {
            className: "aspect-1 mx-auto max-h-72 max-w-md object-cover",
            src: preview,
            alt: "preview tickets image"
          }
        ),
        /* @__PURE__ */ jsx2(
          Gallery,
          {
            render: ({ open }) => /* @__PURE__ */ jsx2(
              Button,
              {
                theme: "action",
                shape: "outlined",
                className: "absolute bottom-0 m-3 p-2 ltr:right-0 rtl:left-0",
                onClick: open,
                children: /* @__PURE__ */ jsx2(ExpandIcon, { className: "h-5 w-5" })
              }
            ),
            slides: [{ src: preview }]
          }
        )
      ] }),
      ((_c = data == null ? void 0 : data.event) == null ? void 0 : _c.is_seated) && /* @__PURE__ */ jsxs2("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx2(Typography2, { variant: "label-M", children: t("ticketing_event:ticket_details.warning_title") }),
        /* @__PURE__ */ jsx2(Typography2, { variant: "body-S", children: t("ticketing_event:ticket_details.warning_desc") })
      ] })
    ] }),
    /* @__PURE__ */ jsxs2(
      Carousel,
      {
        rootClassName: "space-y-4",
        className: "py-0.5",
        options: { active: (data == null ? void 0 : data.tickets.length) > 1 },
        arrows: "top",
        arrowsClassName: "!pb-2 [&>div]:grow [&>div:last-child]:max-w-fit [&>div:last-child]:hidden [&>div:last-child]:md:flex",
        title: /* @__PURE__ */ jsx2(Typography2, { variant: "heading-S", children: t("ticketing_event:ticket_details.types_total", {
          count: (_d = data == null ? void 0 : data.meta) == null ? void 0 : _d.total
        }) }),
        children: [
          data.tickets.map((ticket) => {
            var _a2;
            return /* @__PURE__ */ jsxs2(Paper, { className: "me-4 shrink-0 grow-0 basis-72 space-y-2", children: [
              /* @__PURE__ */ jsxs2("div", { className: "flex justify-between", children: [
                /* @__PURE__ */ jsx2(TicketingIcon_default, { ticket }),
                /* @__PURE__ */ jsx2(
                  ResponsiveModal,
                  {
                    size: "2xl",
                    cancel: false,
                    action: {
                      label: t("common:ok"),
                      onClick: ({ close }) => close()
                    },
                    render: ({ open }) => /* @__PURE__ */ jsx2(Button, { theme: "transparent", shape: "text", className: "p-0", onClick: open, children: /* @__PURE__ */ jsx2(ExclamationIcon, { className: "h-6 w-6" }) }),
                    body: () => {
                      var _a3, _b2;
                      return /* @__PURE__ */ jsxs2("div", { className: "space-y-6", children: [
                        /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-4", children: [
                          /* @__PURE__ */ jsx2(TicketingIcon_default, { ticket }),
                          /* @__PURE__ */ jsx2(Typography2, { variant: "heading-L", children: ticket.title })
                        ] }),
                        ticket.subscription_ticket_type && /* @__PURE__ */ jsx2(TicketTypeName_default, { zone: (_a3 = data == null ? void 0 : data.event) == null ? void 0 : _a3.zone, ticket, showIcon: true }),
                        !!ticket.description && /* @__PURE__ */ jsx2("div", { dangerouslySetInnerHTML: { __html: ticket.description } }),
                        !!((_b2 = ticket == null ? void 0 : ticket.gallery_images) == null ? void 0 : _b2.length) && /* @__PURE__ */ jsx2(
                          Carousel,
                          {
                            className: "py-0.5",
                            options: { active: ticket.gallery_images.length > 1 },
                            children: ticket.gallery_images.map((item) => /* @__PURE__ */ jsx2("div", { className: "aspect-1 me-4 shrink-0 basis-28", children: /* @__PURE__ */ jsx2(ImageWithFallback, { src: item }) }, item))
                          }
                        ),
                        /* @__PURE__ */ jsx2(
                          CurrencyPriceLabel,
                          {
                            originalPrice: +ticket.original_price + +ticket.original_price_vat + fee,
                            discountPrice: +ticket.price + +ticket.vat + fee,
                            currency: ticket == null ? void 0 : ticket.currency,
                            zeroAsFree: true
                          }
                        )
                      ] });
                    }
                  }
                )
              ] }),
              /* @__PURE__ */ jsx2(Typography2, { variant: "label-L", children: ticket.title }),
              ticket.subscription_ticket_type && /* @__PURE__ */ jsx2(
                TicketTypeName_default,
                {
                  zone: (_a2 = data == null ? void 0 : data.event) == null ? void 0 : _a2.zone,
                  ticket,
                  typographyProps: { variant: "body-XS" }
                }
              ),
              /* @__PURE__ */ jsx2(
                CurrencyPriceLabel,
                {
                  originalPrice: +ticket.original_price + +ticket.original_price_vat + fee,
                  discountPrice: +ticket.price + +ticket.vat + fee,
                  currency: ticket == null ? void 0 : ticket.currency,
                  zeroAsFree: true
                }
              )
            ] }, ticket._id);
          }),
          isFetchingNextPage ? [1, 2].map((i) => /* @__PURE__ */ jsx2(Paper, { className: "bg-paper-level-2 me-4 shrink-0 grow-0 basis-72 animate-pulse", children: /* @__PURE__ */ jsx2("div", {}) }, i)) : /* @__PURE__ */ jsx2("div", {}),
          hasNextPage && !isFetchingNextPage ? /* @__PURE__ */ jsx2(Paper, { className: "shrink-0 basis-24 overflow-hidden !p-0", children: /* @__PURE__ */ jsx2(
            Button,
            {
              theme: "action",
              shape: "text",
              className: "flex h-full w-full items-center justify-center p-2",
              onClick: () => fetchNextPage(),
              children: /* @__PURE__ */ jsx2(Typography2, { variant: "body-S", className: "text-center", children: t("ticketing_event:load_more") })
            }
          ) }) : /* @__PURE__ */ jsx2("div", {})
        ]
      }
    )
  ] });
};
var TicketDetails_default = TicketDetailsSection;

// src/components/events/EventSidebar.tsx
import { Divider, Paper as Paper3, Typography as Typography4 } from "@wbk/ui";
import { useTranslation as useTranslation4 } from "@wbk/config";
import { QueueView } from "@wbk/queue";
import { SinglePerson, TabbyIconMini } from "@wbk/svg";
import { twMerge as twMerge4 } from "tailwind-merge";
import { useAuth } from "@wbk/auth";

// src/components/button/resell/Status.tsx
import { useTranslation as useTranslation3 } from "@wbk/config";
import { Paper as Paper2, Typography as Typography3 } from "@wbk/ui";
import { twMerge as twMerge3 } from "tailwind-merge";
import { ShieldIcon } from "@wbk/svg";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var ResellStatus = ({ className }) => {
  const { t } = useTranslation3("ticketing_event");
  const { event } = useBooking_default();
  return /* @__PURE__ */ jsxs3(Paper2, { className: twMerge3("gap-S flex items-center", className), children: [
    /* @__PURE__ */ jsxs3("div", { className: "grow", children: [
      /* @__PURE__ */ jsx3(Typography3, { variant: "heading-XS", children: t("ticketing_event:resell.cant_find_tickets") }),
      /* @__PURE__ */ jsxs3(Typography3, { variant: "body-XXS", className: "text-success max-w-md", children: [
        /* @__PURE__ */ jsx3(ShieldIcon, { className: "inline h-4 w-4" }),
        /* @__PURE__ */ jsx3("span", { className: "ms-0.5", children: t("ticketing_event:resell.guarentee") })
      ] })
    ] }),
    /* @__PURE__ */ jsx3(Button_default, { slug: event.slug, className: "shrink-0" })
  ] });
};
var Status_default = ResellStatus;

// src/components/events/EventSidebar.tsx
import { Fragment as Fragment2, jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var EventSidebar = ({ event, buyButtonProps }) => {
  const { user } = useAuth();
  const { t } = useTranslation4("ticketing_event");
  const isSeason = event.slug.includes("--season--");
  const isFanIDRequired = event.is_fan_id_required && !(user == null ? void 0 : user.is_fan_id_verified);
  return /* @__PURE__ */ jsxs4(Paper3, { className: "top-20 z-10 space-y-2 overflow-hidden !p-0 lg:sticky", children: [
    isFanIDRequired && /* @__PURE__ */ jsxs4("div", { className: "bg-tertiary-100 text-tertiary p-M flex items-center gap-2", children: [
      /* @__PURE__ */ jsx4(SinglePerson, { className: "h-4 w-4 shrink-0" }),
      /* @__PURE__ */ jsx4(Typography4, { className: "grow", variant: "label-S", children: t("ticketing_event:fan.id_required") })
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "p-M space-y-4", children: [
      /* @__PURE__ */ jsx4(Typography4, { variant: "heading-L", as: "h2", children: t("ticketing_event:book_spot") }),
      /* @__PURE__ */ jsx4(EventStartPrice_default, { event }),
      /* @__PURE__ */ jsx4(QueueView, { className: "mb-6 lg:hidden" }),
      event.is_notify_me_enabled ? /* @__PURE__ */ jsx4(
        NotifyButton_default,
        {
          className: "h-12 max-w-md font-semibold",
          shape: "solid",
          theme: "action",
          event: {
            __typename: (event == null ? void 0 : event.contentful_entity_type) || "event",
            id: event._id,
            title: event.title,
            slug: event.slug
          }
        }
      ) : /* @__PURE__ */ jsxs4(Fragment2, { children: [
        /* @__PURE__ */ jsxs4("div", { className: "flex flex-col gap-2", children: [
          /* @__PURE__ */ jsx4(
            EventBuyButton_default,
            __spreadValues({
              event,
              className: "max-w-md",
              "data-testid": "book-button"
            }, buyButtonProps)
          ),
          event.is_accessibility_enabled && /* @__PURE__ */ jsx4(
            AccessibilityButton_default,
            {
              className: "mx-0 max-w-md",
              slug: event.slug,
              type: isSeason ? "seasons" : "events"
            }
          )
        ] }),
        /* @__PURE__ */ jsx4(
          Divider,
          {
            className: twMerge4(
              "!my-6",
              (!event.has_resale_tickets || (event == null ? void 0 : event.is_tabby) !== "1") && "hidden"
            )
          }
        ),
        event.has_resale_tickets && /* @__PURE__ */ jsx4(Status_default, { event, theme: "action" }),
        (event == null ? void 0 : event.is_tabby) === "1" && /* @__PURE__ */ jsxs4("div", { className: "gap-S flex items-center p-2", children: [
          /* @__PURE__ */ jsx4(TabbyIconMini, { className: "h-6 w-6 shrink-0" }),
          /* @__PURE__ */ jsxs4("div", { children: [
            /* @__PURE__ */ jsx4(Typography4, { variant: "label-M", className: "flex items-center gap-2", children: t("ticketing_event:tabby.title") }),
            /* @__PURE__ */ jsx4(Typography4, { className: "text-text-secondary", children: t("ticketing_event:tabby.description") })
          ] })
        ] })
      ] })
    ] })
  ] });
};
var EventSidebar_default = EventSidebar;

export {
  TicketDetails_default,
  EventSidebar_default
};
//# sourceMappingURL=chunk-RMQ6J7EQ.mjs.map